import {
  ADD_MISSING_OVERRIDES_PERMISSIONS,
  DELTA_OVERRIDES_TENANT_SETTINGS,
  EDIT_HEAT_RECIPE_PERMISSIONS,
  HEAT_QUEUE_PERMISSIONS,
  OVERRIDE_HISTORY_PERMISSIONS,
  OVERRIDE_RECIPES_PERMISSIONS,
  getOverridesPermissions,
  overrideHistoryEditingEnabled,
  OVERRIDES_TENANT_SETTINGS,
} from "components/heatQueue/permissions";
import { PlanIdBoundary } from "components/common/boundary/PlanId";
import { PeriodTabs } from "components/common/periodTabs/tabs";
import { NotFound } from "components/notFound";
import { SearchContextProvider } from "contexts/search/SearchContextProvider";
import { useIsAdmin, useTenant } from "hooks/settings";
import { Navigate, Outlet, Route, Routes } from "react-router";
import { ConstraintsPage } from "src/components/constraints/constraintsPage";
import { DebugPage } from "src/components/debug/debugPage";
import { DeployedPlansPage } from "src/components/deployedPlans/deployedPlansPage";
import { PlanPage } from "src/components/plan/PlanPage";
import { EditAndSendPlanPage } from "src/components/deployPlan/EditAndSendPlanPage";
import { SearchPage } from "src/components/search/SearchPage";
import { SearchesPage } from "src/components/searches/SearchesPage";
import { SessionRedirect } from "./sessionRedirect";
import { FileExplorerPage } from "components/file/explorer/FileExplorerPage";
import { FileUploadPage } from "components/file/upload/FileUploadPage";
import {
  RequireFeatureFlagsPage,
  RequireHeatOverridesEnabledPage,
  RequireManager,
  RequirePermissionsPage,
  RequireTenantSettings,
} from "./authentication";
import { InventoryManagementPage } from "components/inventory/InventoryManagementPage";
import { DefaultParametersPage } from "components/defaultParameters/DefaultParametersPage";
import { LabellerAccuracyPage } from "components/labellerAccuracy/LabellerAccuracyPage";
import { ManageLabellersPage } from "components/manageLabellers/page";
import { HeatQueuePage } from "components/heatQueue/HeatQueuePage";
import { OverrideGuidePage } from "components/heatQueue/guide/OverrideGuidePage";
import { OVERRIDE_GUIDE_PATH } from "components/heatQueue/guide/routes";
import { OverridesPage } from "components/heatQueue/override/OverridesPage";
import { EditOverridePage } from "components/heatQueue/override/EditOverridePage";
import { OverrideHistoryPage } from "components/heatQueue/override/OverrideHistoryPage";
import { AddMissingOverridesPage } from "components/heatQueue/override/AddMissingOverridesPage";
import {
  OVERRIDES_PATH,
  OVERRIDE_ADD_MISSING_PATH,
  OVERRIDE_HISTORY_PATH,
  OVERRIDE_RECIPES_PATH,
} from "components/heatQueue/override/routes";
import { usePermission } from "hooks/permissions";
import { useTenantDataOptional } from "contexts/tenant";

const OverridesIndexRedirect = () => {
  const tenantData = useTenantDataOptional();
  const canViewHistory = usePermission(OVERRIDE_HISTORY_PERMISSIONS);
  const canManageRecipes = usePermission(OVERRIDE_RECIPES_PERMISSIONS);
  const canAddMissing =
    usePermission(ADD_MISSING_OVERRIDES_PERMISSIONS) &&
    overrideHistoryEditingEnabled(tenantData);
  return (
    <Navigate
      to={
        canViewHistory
          ? OVERRIDE_HISTORY_PATH
          : canAddMissing && !canManageRecipes
            ? OVERRIDE_ADD_MISSING_PATH
            : OVERRIDE_RECIPES_PATH
      }
      replace
    />
  );
};

export const TenantRoutes = () => {
  const tenant = useTenant();
  const isAdmin = useIsAdmin();
  const tenantData = useTenantDataOptional();

  return (
    <Routes>
      <Route
        element={
          <RequireFeatureFlagsPage featureFlags={["optimiser"]}>
            <RequirePermissionsPage permissions={["read"]}>
              <Outlet />
            </RequirePermissionsPage>
          </RequireFeatureFlagsPage>
        }
      >
        <Route path="optimiser" element={<SearchesPage />} />
        <Route path="deployed-plans" element={<DeployedPlansPage />} />

        <Route
          path="constraints"
          element={
            <SearchContextProvider>
              <ConstraintsPage />
            </SearchContextProvider>
          }
        />

        <Route
          path="search"
          element={
            <SearchContextProvider>
              <SearchPage />
            </SearchContextProvider>
          }
        />
        {isAdmin ? (
          <Route
            path="debug-search"
            element={
              <SearchContextProvider>
                <DebugPage />
              </SearchContextProvider>
            }
          />
        ) : null}

        <Route
          path="plan"
          element={
            <SearchContextProvider>
              <PlanIdBoundary fallback={<Navigate to={`/${tenant}`} />}>
                <PeriodTabs>
                  <PlanPage />
                </PeriodTabs>
              </PlanIdBoundary>
            </SearchContextProvider>
          }
        />

        <Route
          path="deploy-plan"
          element={
            <SearchContextProvider>
              <PlanIdBoundary fallback={<Navigate to={`/${tenant}`} />}>
                <EditAndSendPlanPage />
              </PlanIdBoundary>
            </SearchContextProvider>
          }
        />

        <Route path="session/:session_id" element={<SessionRedirect />} />
      </Route>

      <Route
        path="file-upload"
        element={
          <RequirePermissionsPage permissions={["read"]}>
            <FileUploadPage />
          </RequirePermissionsPage>
        }
      />
      {isAdmin ? (
        <Route path="file-explorer" element={<FileExplorerPage />} />
      ) : null}

      <Route
        path="inventory"
        element={
          <RequireFeatureFlagsPage featureFlags={["inventory_measurement"]}>
            <RequirePermissionsPage permissions={["read"]}>
              <InventoryManagementPage />
            </RequirePermissionsPage>
          </RequireFeatureFlagsPage>
        }
      />
      <Route
        path="parameters/chemistry"
        element={
          <Navigate to={`/${tenant}/parameters/material-properties`} replace />
        }
      />
      <Route
        path="parameters/:tabKey?"
        element={
          <RequireFeatureFlagsPage featureFlags={["optimiser"]}>
            <RequireManager>
              <DefaultParametersPage />
            </RequireManager>
          </RequireFeatureFlagsPage>
        }
      />

      <Route
        path="manage-labellers"
        element={
          <RequirePermissionsPage permissions={["manage_labellers"]}>
            <RequireFeatureFlagsPage featureFlags={["scrap_eyes"]}>
              <ManageLabellersPage />
            </RequireFeatureFlagsPage>
          </RequirePermissionsPage>
        }
      />

      <Route
        path="labellers/:labellerId/accuracy"
        element={
          <RequirePermissionsPage permissions={["manage_labellers"]}>
            <RequireFeatureFlagsPage featureFlags={["scrap_eyes"]}>
              <LabellerAccuracyPage />
            </RequireFeatureFlagsPage>
          </RequirePermissionsPage>
        }
      />

      <Route
        path="heat-queue"
        element={
          <RequireHeatOverridesEnabledPage>
            <RequirePermissionsPage permissions={HEAT_QUEUE_PERMISSIONS}>
              <HeatQueuePage />
            </RequirePermissionsPage>
          </RequireHeatOverridesEnabledPage>
        }
      />

      <Route
        path={`heat-queue/${OVERRIDE_GUIDE_PATH}`}
        element={
          <RequireTenantSettings settings={DELTA_OVERRIDES_TENANT_SETTINGS}>
            <RequirePermissionsPage permissions={EDIT_HEAT_RECIPE_PERMISSIONS}>
              <OverrideGuidePage />
            </RequirePermissionsPage>
          </RequireTenantSettings>
        }
      />

      <Route
        path={OVERRIDES_PATH}
        element={
          <RequireHeatOverridesEnabledPage>
            <RequirePermissionsPage
              permissions={getOverridesPermissions(tenantData)}
            >
              <Outlet />
            </RequirePermissionsPage>
          </RequireHeatOverridesEnabledPage>
        }
      >
        <Route index element={<OverridesIndexRedirect />} />
        <Route
          path={OVERRIDE_HISTORY_PATH}
          element={
            <RequirePermissionsPage permissions={OVERRIDE_HISTORY_PERMISSIONS}>
              <OverrideHistoryPage />
            </RequirePermissionsPage>
          }
        />
        <Route
          path={OVERRIDE_RECIPES_PATH}
          element={
            <RequireTenantSettings settings={OVERRIDES_TENANT_SETTINGS}>
              <RequirePermissionsPage
                permissions={OVERRIDE_RECIPES_PERMISSIONS}
              >
                <Outlet />
              </RequirePermissionsPage>
            </RequireTenantSettings>
          }
        >
          <Route index element={<OverridesPage />} />
          <Route path=":fixedMixSetId/edit" element={<EditOverridePage />} />
        </Route>
        <Route
          path={OVERRIDE_ADD_MISSING_PATH}
          element={
            overrideHistoryEditingEnabled(tenantData) ? (
              <RequirePermissionsPage
                permissions={ADD_MISSING_OVERRIDES_PERMISSIONS}
              >
                <AddMissingOverridesPage />
              </RequirePermissionsPage>
            ) : (
              <NotFound />
            )
          }
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
